import { AlertCircle, Calendar, CreditCard, Tag } from 'lucide-react';
import React from 'react';

import { ContentRegistry } from '../registries';
import { ContentItemProps } from '../registries/ContentRegistry';

const MembershipCard: React.FC<ContentItemProps> = ({
  content,
  variant = 'card',
  onAction,
  className = '',
}) => {
  const isMinimal = variant === 'minimal';

  // Extract membership data
  const {
    productName,
    discountPercentage,
    membershipAssetDetails,
    isMember,
    membership,
    vendorName,
    vendorLogo,
  } = content.data || {};

  // Calculate days remaining if member
  const getDaysRemaining = () => {
    if (!membership?.endDate) return null;
    const endDate = new Date(membership.endDate);
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const daysRemaining = getDaysRemaining();
  const isApproachingExpiry = daysRemaining !== null && daysRemaining < 30;

  // Handle action
  const handleShowBenefits = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAction?.('show-benefits', content);
  };

  // Minimal variant - Simple status indicator
  if (isMinimal) {
    return (
      <div
        className={`inspect-theme-bg-elevated rounded-lg p-3 cursor-pointer hover:inspect-theme-shadow transition-all ${className}`}
        onClick={handleShowBenefits}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg inspect-theme-bg">
            <Tag className="w-4 h-4 inspect-theme-text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium inspect-theme-text">
              {isMember ? 'Active Member' : `Save ${discountPercentage}%`}
            </p>
            {isMember && isApproachingExpiry && (
              <p className="text-xs inspect-theme-text-muted">Expires in {daysRemaining} days</p>
            )}
          </div>
          <div className="text-xs inspect-theme-text-primary font-medium">View →</div>
        </div>
      </div>
    );
  }

  // Card and Expanded variants
  return (
    <div className={`inspect-theme-bg-elevated rounded-xl overflow-hidden ${className}`}>
      {/* Header with vendor branding */}
      <div className="p-4 border-b inspect-theme-border">
        <div className="flex items-center gap-3">
          {vendorLogo && (
            <img src={vendorLogo} alt={vendorName} className="w-10 h-10 rounded-lg object-cover" />
          )}
          <div className="flex-1">
            <h4 className="font-medium inspect-theme-text">{vendorName || 'Membership'}</h4>
            <p className="text-sm inspect-theme-text-muted">
              {isMember ? 'Active Member' : 'Membership Benefits'}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {isMember ? (
          // Member state
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 inspect-theme-text-muted" />
              <span className="text-sm font-mono inspect-theme-text">{membership?.shortId || 'Member'}</span>
            </div>

            {membership?.endDate && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 inspect-theme-text-muted" />
                <span className="text-sm inspect-theme-text">
                  {isApproachingExpiry ? (
                    <span className="inspect-theme-text-warning flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Expires in {daysRemaining} days
                    </span>
                  ) : (
                    `Valid until ${new Date(membership.endDate).toLocaleDateString()}`
                  )}
                </span>
              </div>
            )}

            <div className="pt-2">
              <p className="text-sm inspect-theme-text-muted mb-3">Your exclusive discounts</p>
              {productName && discountPercentage && (
                <div className="p-3 rounded-lg inspect-theme-bg">
                  <p className="text-sm font-medium inspect-theme-text">
                    {discountPercentage}% off {productName}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Guest/Non-member state
          <div className="space-y-3">
            {productName && discountPercentage && (
              <div className="p-3 rounded-lg inspect-theme-bg-primary/10 border inspect-theme-border-primary">
                <p className="text-lg font-semibold inspect-theme-text-primary text-center">
                  Save {discountPercentage}% on {productName}
                </p>
              </div>
            )}

            <p className="text-sm inspect-theme-text-muted text-center">
              Unlock exclusive member pricing and benefits
            </p>

            {variant === 'expanded' && membershipAssetDetails && (
              <div className="pt-2 space-y-2">
                <div className="p-3 rounded-lg inspect-theme-bg">
                  <p className="text-xs inspect-theme-text-muted uppercase tracking-wide mb-1">
                    Membership includes
                  </p>
                  <ul className="text-sm inspect-theme-text space-y-1">
                    <li>• Exclusive member pricing</li>
                    <li>• Early access to new products</li>
                    <li>• Special member events</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action button */}
        <button
          onClick={handleShowBenefits}
          className="w-full mt-4 px-4 py-2 rounded-lg inspect-theme-button-primary text-sm font-medium transition-all hover:opacity-90"
        >
          Show membership benefits
        </button>
      </div>

      {/* Footer indicator for approaching expiry */}
      {isMember && isApproachingExpiry && (
        <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-500"></div>
      )}
    </div>
  );
};

// Auto-register with ContentRegistry
ContentRegistry.register('membership', MembershipCard);

export default MembershipCard;
